import { useState } from "react";
import {
  Box, Typography, Paper, Stack, Button, Chip, MenuItem, TextField, Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { stockAdjustmentsApi, type StockAdjustmentListDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
const STATUS = ["", "Draft", "Submitted", "Posted", "Cancelled"];
const STATUS_COLOR: Record<string, "default" | "warning" | "info" | "success" | "error"> = {
  Draft: "default", Submitted: "warning", Approved: "info", Posted: "success", Cancelled: "error",
};

export function StockAdjustmentsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const canCreate = useAuth(s => s.hasPermission("inventory.adjustment.create"));

  const query = useQuery({
    queryKey: ["stock-adjustments", status, pagination.page, pagination.pageSize],
    queryFn: () => stockAdjustmentsApi.list({
      status: status || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const columns: GridColDef<StockAdjustmentListDto>[] = [
    { field: "documentNo", headerName: "Document #", width: 160 },
    { field: "documentDate", headerName: "Date", width: 110,
      valueFormatter: (v) => v ? new Date(v as string).toLocaleDateString() : "" },
    { field: "warehouseName", headerName: "Warehouse", width: 180 },
    { field: "reason", headerName: "Reason", flex: 1, minWidth: 220 },
    {
      field: "status", headerName: "Status", width: 110,
      renderCell: (p) => <Chip size="small" label={p.value} color={STATUS_COLOR[p.value as string] ?? "default"} />,
    },
    { field: "lineCount", headerName: "Lines", width: 80, type: "number" },
    {
      field: "totalAmount", headerName: "Value impact", width: 140, type: "number",
      valueFormatter: (v) => inr.format(Number(v ?? 0)),
    },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Stock adjustments</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => navigate("/inventory/adjustments/new")}>
            New Adjustment
          </Button>
        )}
      </Stack>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}
            size="small" sx={{ minWidth: 160 }}>
            {STATUS.map((s) => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
          </TextField>
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowClick={(p) => navigate(`/inventory/adjustments/${p.id}`)}
            sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
